import React, {Fragment, useState, useEffect, useRef} from 'react';

import {useFeature} from '@optimizely/react-sdk';
import {get, assign, trim, remove} from 'lodash-es';
import PropTypes from 'prop-types';

import {
  GLOBAL_NAV_SEARCH_FORM_ID,
  MAX_NUMBER_OF_RECENT_SEARCHES,
  TIME_DELAY_TO_FETCH_SUGGESTIONS,
} from 'static/three-oh/src/routes/Search/constants';
import AgathaSuggestionsContainer from 'static/three-oh/src/routes/SearchBeta/components/AgathaSuggestionsContainer/AgathaSuggestionsContainer';
import {eventStream, getEventNameForEventStream} from 'static/three-oh/utils/sessionActivity';
import useDebounce from 'static/three-oh/utils/useDebounce';

import {INACTIVE_INDEX_VALUE} from '../../constants';
import GlobalNavContentDrawer from '../GlobalNavContentDrawer/GlobalNavContentDrawer';
import GlobalNavSearchForm from './GlobalNavSearchForm/GlobalNavSearchForm';
import JeevesSearchSuggestions from './JeevesSearchSuggestions/JeevesSearchSuggestions';
import styles from './styles';

const componentDisplayName = 'GlobalNavSearch';
const propTypes = {
  isDesktopDisplay: PropTypes.bool.isRequired,
  handleOnCancel: PropTypes.func.isRequired,
  handleOnClickOff: PropTypes.func.isRequired,
  handleOnEscape: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  setOpenIndex: PropTypes.func.isRequired,
  show: PropTypes.bool,
  userProperties: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

const defaultProps = {
  isDesktopDisplay: false,
};

const GlobalNavSearch = React.forwardRef((props, ref) => {
  const {searchButtonRef, browseButtonRef} = ref;
  const [queryTerm, setQueryTerm] = useState('');
  const [suggestionsAreActive, setActiveStateOfSuggestions] = useState(false);
  const [suggestionsAreVisible, setVisibilityOfSuggestions] = useState(false);
  const [suggestions, updateSuggestions] = useState([]);
  const recentSearchTerms = 'recentSearchTerms';

  // The suggestions generated by the Agatha API are visible to users in the
  // 'test' segment of an A/B test housed by Optimizely.
  const [isEnabled] = useFeature(
    'agatha_search_suggestions',
    {autoUpdate: true},
  );

  useEffect(() => {
    const {isAuthenticated} = props.userProperties;
    const initializeRecentSearchStorage = !sessionStorage.getItem(recentSearchTerms);
    if (isAuthenticated && initializeRecentSearchStorage) {
      // Values stored in the 'sessionStorage' property of the browser's
      // 'window' object have to be stored as a string.
      sessionStorage.setItem(recentSearchTerms, '');
    }
  }, []); // Do this once -- when the component mounts.

  // Update the list of recent searches stored in sessionStorage.
  const updateRecentSearchList = () => {
    const recentSearchTermsString = sessionStorage.getItem(recentSearchTerms);
    const recentSearchTermsArray = recentSearchTermsString.split(',');
    const queryTermAlreadyListed = recentSearchTermsArray.includes(queryTerm);
    const queryIsPresent = Boolean(trim(queryTerm));

    if (queryIsPresent && !queryTermAlreadyListed) {
      recentSearchTermsArray.push(queryTerm);
    }

    // Remove falsey inputs created by blank strings.
    remove(recentSearchTermsArray, (term) => !term);

    if (recentSearchTermsArray.length > MAX_NUMBER_OF_RECENT_SEARCHES) {
      recentSearchTermsArray.shift();
    }
    sessionStorage.setItem('recentSearchTerms', recentSearchTermsArray);
  };

  const trackUserEvent = (eventName, properties = {}, click = true) => {
    const eventType = click ? 'click' : 'keydown';
    const componentProperties = {
      user_invoked_search: true
    };
    const eventProperties = assign(componentProperties, properties);

    const newEventProps = {
      event_name: getEventNameForEventStream('Content', 'Initiate', 'Search'),
      user_id: props.user.id,
      dimension_properties: {
        description: `Global nav search query term: ${eventName}`,
        legacy_event_name: `query_term_${eventName}`,
      }
    };
    eventStream(newEventProps, {userId: get(props, 'user.id'), name: `query_term_${eventName}`, type: eventType, properties: eventProperties});
  };

  const onQueryTermSubmission = (query) => {
    const {userProperties, history} = props;
    const isStandard = get(query, 'source_object_type') === 'Standard';
    const userIsAuthenticated = get(userProperties, 'isAuthenticated');
    const queryIsPresent = isStandard || Boolean(trim(query));

    // Put the query on sessionStorage.
    if (userIsAuthenticated && queryIsPresent) {
      updateRecentSearchList();
    }

    const properties = {
      user_id: props.user.id,
      active_term: query || ''
    };
    trackUserEvent('submit', properties, true);

    let url;

    if (isStandard) {
      url = `/search?needle=&standard_ids=${query.source_object_id}`;
    } else {
      url = `/search?needle=${query}`;
    }

    history.push(url);
  };

  const onSuggestionSelection = (suggestion) => {
    const {userProperties, history} = props;
    const {isAuthenticated} = userProperties;
    const queryIsPresent = Boolean(trim(suggestion.text));

    // Put the query on sessionStorage.
    if (isAuthenticated && queryIsPresent) {
      updateRecentSearchList();
    }

    // Send data about this selection to Kinesis.
    const legacyEventProperties = {
      objectType: 'Search Suggestion',
      actionPrefix: 'Initiate',
      actionName: 'Search',
      captured_url: window.location.pathname,
      trackImpression: true,
      eventProperties: {
        suggestions_id: suggestion.suggestionId,
        search_term: suggestion.text,
      },
    };
    const eventStreamProperties = {
      description: `The ${suggestion.text} suggestion was selected when this fragment appeared in the search form: ${queryTerm}`,
      input_needle: queryTerm,
      suggestion_text: suggestion.text,
      current_url: window.location.pathname,
      application: 'agatha',
      search_id: suggestion.suggestionId,
      schema_version: '1.0',
      component_type: componentDisplayName,
    };
    eventStream(eventStreamProperties, legacyEventProperties);

    // We transport the value of the suggestion's ID via react-router's
    // 'state' property so that the Search page can access and incorporate
    // the param into the request sent to the v3 Search API.
    history.push({
      pathname: '/search',
      search: `?needle=${suggestion.text}`,
      state: {
        suggestionId: suggestion.suggestionId,
      },
    });
  };

  // Handle dismissal of the suggestion list.
  const onSuggestionDismiss = () => {
    setActiveStateOfSuggestions(false);
    setVisibilityOfSuggestions(false);
    searchButtonRef.current.focus();
  };

  // Handle a search-suggestion's 'tab' event.
  const onSuggestionTab = () => {
    const inputFieldElement = document.getElementById(GLOBAL_NAV_SEARCH_FORM_ID);
    if (inputFieldElement) {
      inputFieldElement.focus();
    }
  };

  const debouncedSearchTerm = useDebounce(queryTerm, TIME_DELAY_TO_FETCH_SUGGESTIONS);

  const searchContentDrawerRef = useRef(null);
  const searchForm = (
    <GlobalNavSearchForm
      queryTerm={queryTerm}
      setQueryTerm={setQueryTerm}
      user={props.user}
      userProperties={props.userProperties}
      history={props.history}
      setVisibilityOfSuggestions={setVisibilityOfSuggestions}
      setActiveStateOfSuggestions={setActiveStateOfSuggestions}
      isDesktopDisplay={props.isDesktopDisplay}
      onQueryTermSubmission={onQueryTermSubmission}
      updateSuggestions={updateSuggestions}
      ref={{
        searchButtonRef: searchButtonRef,
        browseButtonRef: browseButtonRef,
        searchContentDrawerRef: searchContentDrawerRef,
      }}
      handleOnEscape={props.handleOnEscape}
      handleOnCancel={props.handleOnCancel}
    />
  );
  const jeevesSuggestions = (
    <JeevesSearchSuggestions
      user={props.user}
      userProperties={props.userProperties}
      history={props.history}
      suggestionsAreActive={suggestionsAreActive}
      suggestionsAreVisible={suggestionsAreVisible}
      throttledQueryTerm={debouncedSearchTerm}
      onSuggestionDismiss={onSuggestionDismiss}
      onSuggestionTab={onSuggestionTab}
      trackUserEvent={trackUserEvent}
      onQueryTermSubmission={onQueryTermSubmission}
    />
  );
  const agathaSuggestions = (
    <AgathaSuggestionsContainer
      isVisible={suggestionsAreVisible}
      needleForSuggestionsFetch={debouncedSearchTerm}
      updateSuggestionsAreVisible={setVisibilityOfSuggestions}
      onSuggestionSelection={onSuggestionSelection}
      updateSuggestions={updateSuggestions}
      suggestions={suggestions}
      isGlobalNavSearch
    />
  );
  const desktopSearchDisplay = (
    <>
      {props.show ? searchForm : null}
      <GlobalNavContentDrawer
        show={props.show}
        additionalClassName={styles.contentDrawer}
        onCancel={() => props.handleOnCancel(searchButtonRef)}
        onClickOff={(event) => props.handleOnClickOff(event, searchButtonRef)}
        onEscape={() => {
          props.setOpenIndex(INACTIVE_INDEX_VALUE);
          props.handleOnEscape(searchContentDrawerRef, searchButtonRef);
        }}
        ref={searchContentDrawerRef}
        isSearchContentDrawer
      >
        {isEnabled ? agathaSuggestions : jeevesSuggestions}
      </GlobalNavContentDrawer>
    </>
  );
  const mobileSearchDisplay = (
    <GlobalNavContentDrawer
      show={props.show}
      onCancel={() => props.handleOnCancel(searchButtonRef)}
      onClickOff={(event) => props.handleOnClickOff(event, searchButtonRef)}
      onEscape={() => {
        props.setOpenIndex(INACTIVE_INDEX_VALUE);
        props.handleOnEscape(searchContentDrawerRef, searchButtonRef);
      }}
      ref={searchContentDrawerRef}
    >
      <div className={styles.mobileContainer}>
        {searchForm}
        {isEnabled ? agathaSuggestions : jeevesSuggestions}
      </div>
    </GlobalNavContentDrawer>
  );

  return props.isDesktopDisplay ? desktopSearchDisplay : mobileSearchDisplay;
});

GlobalNavSearch.propTypes = propTypes;
GlobalNavSearch.componentDisplayName = componentDisplayName;
GlobalNavSearch.defaultProps = defaultProps;

export default GlobalNavSearch;
